/**
 * @engine/vfx — VFX preset builders (single-system group).
 *
 * Pure factories returning VFXGraphData, extracted from VFXGraphAsset.ts.
 * Re-exported from VFXGraphAsset so existing import paths are unchanged.
 */

import type { VFXGraphData } from '../types';
import { regenerateGraphIds } from './_shared';

/**
 * Create a firework VFX graph with preset nodes.
 *
 * Includes:
 * - Burst spawn (50-100 particles)
 * - Random sphere position with small radius
 * - Cone velocity (upward spread)
 * - Gravity and drag
 * - Color over life (orange -> red -> fade)
 * - Size over life (shrink)
 * - Quad output
 */

export function createCosmicAuroraVFX(
  name: string = "Cosmic Aurora",
): VFXGraphData {
  const template: VFXGraphData = {
    id: "steam_cosmic_aurora",
    name: "Cosmic Aurora",
    nodes: [
      {
        id: "spawn_main",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 300 },
        contextId: "ctx_spawn",
      },
      {
        id: "init_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 3, max: 6 },
        contextId: "ctx_init",
      },
      {
        id: "init_position",
        type: "vfx.init.position_line",
        position: { x: 50, y: 150 },
        inputValues: {
          start: { x: -8, y: -2, z: 0 },
          end: { x: 8, y: -2, z: 0 },
        },
        contextId: "ctx_init",
      },
      {
        id: "init_velocity",
        type: "vfx.init.velocity_direction",
        position: { x: 50, y: 250 },
        inputValues: { direction: { x: 0, y: 1, z: 0 }, speed: 2, spread: 25 },
        contextId: "ctx_init",
      },
      {
        id: "init_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 350 },
        inputValues: { min: 0.3, max: 0.8 },
        contextId: "ctx_init",
      },
      {
        id: "init_color",
        type: "vfx.init.color_random",
        position: { x: 50, y: 450 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 0.1, g: 0.9, b: 0.4, a: 1 } },
              { t: 0.3, color: { r: 0.2, g: 0.6, b: 0.9, a: 1 } },
              { t: 0.6, color: { r: 0.6, g: 0.2, b: 0.9, a: 1 } },
              { t: 1, color: { r: 0.9, g: 0.3, b: 0.6, a: 1 } },
            ],
          },
        },
        contextId: "ctx_init",
      },
      {
        id: "update_curl",
        type: "vfx.update.curl_noise",
        position: { x: 50, y: 50 },
        inputValues: { intensity: 4, scale: 0.3, speed: 0.5 },
        contextId: "ctx_update",
      },
      {
        id: "update_turbulence",
        type: "vfx.update.turbulence",
        position: { x: 50, y: 150 },
        inputValues: {
          intensity: 1.5,
          frequency: 0.8,
          octaves: 4,
          roughness: 0.6,
        },
        contextId: "ctx_update",
      },
      {
        id: "update_drag",
        type: "vfx.update.drag",
        position: { x: 50, y: 250 },
        inputValues: { coefficient: 0.3 },
        contextId: "ctx_update",
      },
      {
        id: "update_alpha",
        type: "vfx.update.alpha_over_life",
        position: { x: 50, y: 350 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0 },
              { t: 0.1, v: 0.8 },
              { t: 0.7, v: 0.6 },
              { t: 1, v: 0 },
            ],
          },
        },
        contextId: "ctx_update",
      },
      {
        id: "update_size",
        type: "vfx.update.size_over_life",
        position: { x: 50, y: 450 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0.5 },
              { t: 0.3, v: 1 },
              { t: 0.8, v: 0.8 },
              { t: 1, v: 0.2 },
            ],
          },
          scale: 1.5,
        },
        contextId: "ctx_update",
      },
      {
        id: "output_quad",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1 },
        contextId: "ctx_output",
      },
    ],
    edges: [],
    variables: [],
    contexts: [
      {
        id: "ctx_spawn",
        type: "vfx.spawn",
        position: { x: 50, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_main"],
        collapsed: false,
      },
      {
        id: "ctx_init",
        type: "vfx.initialize",
        position: { x: 50, y: 200 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "init_lifetime",
          "init_position",
          "init_velocity",
          "init_size",
          "init_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update",
        type: "vfx.update",
        position: { x: 50, y: 750 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "update_curl",
          "update_turbulence",
          "update_drag",
          "update_alpha",
          "update_size",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output",
        type: "vfx.output",
        position: { x: 50, y: 1300 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_quad"],
        collapsed: false,
      },
    ],
    contextFlowEdges: [
      {
        id: "flow_spawn_init",
        fromContextId: "ctx_spawn",
        fromPortId: "spawn_event",
        toContextId: "ctx_init",
        toPortId: "spawn_event",
      },
      {
        id: "flow_init_update",
        fromContextId: "ctx_init",
        fromPortId: "particle",
        toContextId: "ctx_update",
        toPortId: "particle",
      },
      {
        id: "flow_update_output",
        fromContextId: "ctx_update",
        fromPortId: "particle",
        toContextId: "ctx_output",
        toPortId: "particle",
      },
    ],
    contextSettings: { ctx_init: { capacity: 5000, boundsMode: "auto" } },
    capacity: { maxParticles: 5000, boundsMode: "auto" },
    simulation: { space: "world", deltaTimeMode: "deltaTime" },
    rendering: { sortMode: "none", blendMode: "additive" },
  };
  return regenerateGraphIds(template, name);
}

/**
 * Electric Storm - Plasma bolts with attraction, bursts, and line output.
 * Multi-system with plasma, core glow, and spark bursts.
 */
